import { DropdownItemRenderV2 } from "#src/commonV2/antdHelpers/DropdownItemRender";
import { DropdownRenderV2 } from "#src/commonV2/antdHelpers/DropdownRender";
import { ButtonV2 } from "#src/commonV2/Button";
import { IFilterItemV2 } from "#src/commonV2/Table/Filter/types";
import constants from "#src/config/constants";
import useFetchPaginated from "#src/hooks/useFetchPaginated";
import resourceServices from "#src/services/resource";
import { Dropdown } from "@lockerpm/design";
import { XClose } from "@untitled-ui/icons-react";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

interface ISoftwareFilterProps {
  workspaceId: string;
  searchParamKey?: string;
  nameParamKey?: string;
  software: IFilterItemV2 | null;
  setSoftware: Dispatch<SetStateAction<IFilterItemV2 | null>>;
}

/** Work-around for the problem of this filter have A F*CKING LOT of option items. */
export const SoftwareFilter = ({
  workspaceId,
  searchParamKey,
  nameParamKey,
  software,
  setSoftware,
}: ISoftwareFilterProps) => {
  const { t } = useTranslation("endpoint", {
    keyPrefix: "devices.table.filter.software",
  });

  const [searchParams, setSearchParams] = useSearchParams();

  const [searchKeyword, setSearchKeyword] = useState<string>("");

  const fetchSoftwareParams = useMemo(
    () =>
      [
        workspaceId,
        {
          page: 1,
          size: constants.DEFAULT_PAGE_SIZE,
          q: searchKeyword,
        },
      ] satisfies Parameters<typeof resourceServices.listEndpointSoftwares>,
    [workspaceId, searchKeyword]
  );

  const { list: softwareList } = useFetchPaginated(
    resourceServices.listEndpointSoftwares,
    fetchSoftwareParams
  );

  const options: IFilterItemV2[] = useMemo(() => {
    return softwareList
      ? softwareList.map((item) => ({
          key: item.id.toString(),
          value: item.id.toString(),
          getLabel: () => item.name,
        }))
      : [];
  }, [softwareList]);

  const onChangeSelection = (newSelection: IFilterItemV2 | null) => {
    setSoftware(newSelection);
    if (newSelection && newSelection.getLabel()) {
      setSearchKeyword(newSelection.getLabel());
    }
    setSearchParams((params) => {
      if (searchParamKey) {
        if (newSelection && newSelection.value) {
          params.set(searchParamKey, newSelection.value);
        } else {
          params.delete(searchParamKey);
        }
      }
      if (nameParamKey) {
        if (newSelection && newSelection.getLabel()) {
          params.set(nameParamKey, newSelection.getLabel());
        } else {
          params.delete(nameParamKey);
        }
      }
      return params;
    });
  };

  const finalSelected: IFilterItemV2 | null = useMemo(() => {
    if (searchParamKey) {
      const searchParamValue = searchParams.get(searchParamKey);
      const findItem = options.find((item) => {
        return item.value === searchParamValue;
      });

      if (findItem) {
        return findItem;
      }
      if (searchParamValue) {
        const initName = nameParamKey
          ? (searchParams.get(nameParamKey) ?? searchParamValue)
          : searchParamValue;

        return {
          key: searchParamValue,
          value: searchParamValue,
          getLabel: () => initName,
        };
      }
      return null;
    }
    return software;
  }, [searchParamKey, nameParamKey, searchParams, options, software]);

  useEffect(() => {
    const searchValue = searchParamKey
      ? (searchParams.get(searchParamKey) ?? "")
      : "";
    const initName = nameParamKey
      ? (searchParams.get(nameParamKey) ?? searchValue)
      : "";
    setSearchKeyword(initName);
  }, [searchParamKey, nameParamKey, searchParams]);

  return finalSelected ? (
    <Dropdown
      menu={{
        items: options.map((item) => ({
          key: item.key,
          value: item.value,
          label: (
            <DropdownItemRenderV2
              label={
                <>
                  {item.leftIcon ? item.leftIcon : null}
                  {item.getLabel()}
                </>
              }
              isSelected={item.key === finalSelected.key}
            />
          ),
        })),
        onClick: (obj) => {
          const newSelected = options.find((item) => item.key === obj.key);
          onChangeSelection(newSelected ? newSelected : null);
        },
      }}
      dropdownRender={(menu) => <DropdownRenderV2>{menu}</DropdownRenderV2>}
      placement="bottomRight"
      trigger={["click"]}
    >
      <ButtonV2
        variant="secondary"
        color={"brand"}
        className="outline outline-2 outline-offset-2 outline-transparent focus:outline-brand-v2-400"
      >
        <div className="flex items-center gap-1">
          <span>{t("label")}:</span>
          <span className="relative flex items-center">
            <input
              className="absolute left-0 z-10 w-full outline-none bg-transparent font-semibold"
              value={searchKeyword}
              onChange={(e) => {
                setSearchKeyword(e.target.value);
              }}
              onBlur={() => {
                if (finalSelected) {
                  setSearchKeyword(finalSelected.getLabel());
                }
              }}
            />
            <p className="opacity-0 pointer-events-none">{searchKeyword}</p>
          </span>
          <XClose
            className="h-5 w-5"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onChangeSelection(null);
            }}
          />
        </div>
      </ButtonV2>
    </Dropdown>
  ) : null;
};
